import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import * as React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { PageContainer } from "@/components/layout/PageContainer";
import { Admonition } from "@/components/ui/admonition";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { isoToDate } from "@/components/ui/date-picker.utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormMessageT } from "@/features/contract-form/components/FormMessageT";
import { EngagementClosingFxRatesSection } from "@/features/engagement/components/EngagementClosingFxRatesSection";
import { OpeningBalanceModeRadio } from "@/features/engagement/components/OpeningBalanceModeRadio";
import { useEngagementAccess } from "@/features/engagement/useEngagementAccess";
// Phase 6e.4.2 — MagicSyncButton + FxSyncAuditAccordion moved to
// ContractListPage. The engagement form no longer mounts them.
import {
  engagementCreationSchema,
  type EngagementCreationFormValues,
} from "@/schemas/engagement-creation";
import { useHistoryStorage } from "@/services";
import type { EngagementHistoryEntry } from "@/types";

const CURRENCY_CODES = ["TRY", "USD", "EUR", "GBP"] as const;

function isoDate(d: Date): string {
  // Use UTC components to avoid TZ off-by-one when crossing local midnight.
  const yyyy = d.getUTCFullYear().toString().padStart(4, "0");
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const dd = d.getUTCDate().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Phase 6a.19a — Big4 audit reality is that engagements target the
// PREVIOUS calendar year (Year-N audit done in Year-N+1). Default the
// form to the full prior year so the auditor doesn't manually re-enter
// `2024-01-01` → `2024-12-31` on every new engagement.
function defaultStart(): Date {
  const prevYear = new Date().getUTCFullYear() - 1;
  return new Date(Date.UTC(prevYear, 0, 1));
}

function defaultEnd(): Date {
  const prevYear = new Date().getUTCFullYear() - 1;
  return new Date(Date.UTC(prevYear, 11, 31));
}

/**
 * EngagementCreationPage — Phase 6a.9 per Frontend Spec §21.6.
 *
 * Stage 1 of the engagement lifecycle: collect client metadata, audit
 * window, functional currency, opening-balance mode, and (if MANUAL)
 * three opening balances. On submit, persist a new
 * `EngagementHistoryEntry` to localStorage with `contractCount: 0` and
 * navigate to the contract-list page where contracts get added.
 *
 * The engagement endpoint is NOT called from this page — calculation
 * happens in Phase 6a.11 once contracts are entered.
 *
 * Frontend validation intentionally omits the 18-month max audit-period
 * check so the auditor receives the canonical backend error message
 * (Spec §20.3 `error.engagementPeriodTooLong`) rather than a frontend
 * paraphrase. All other invariants (period start < end, MANUAL ⇒
 * openingBalances) are enforced client-side for immediate feedback.
 */
export function EngagementCreationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const historyStorage = useHistoryStorage();

  // Phase 6a.19a — dual-mode page. `useParams().id` is set on
  // `/engagements/:id/edit`; absent on `/engagements/new`. Edit mode
  // additionally loads the persisted entry and threads it through
  // form.reset + the save handler's upsert path.
  const { id: editId } = useParams<{ id?: string }>();
  const isEditMode = Boolean(editId);
  const [loadedEntry, setLoadedEntry] =
    React.useState<EngagementHistoryEntry | null>(null);

  // SPEC-SHARE-B2 — read-only applies ONLY in edit mode (create mode is the
  // user making their own engagement, never a viewer). `editId` is undefined
  // in create mode, so the hook short-circuits without a storage call.
  const { readOnly: accessReadOnly } = useEngagementAccess(editId);
  const viewerReadOnly = isEditMode && accessReadOnly;

  const [submissionError, setSubmissionError] = React.useState<string | null>(
    null,
  );
  const [submissionSuccess, setSubmissionSuccess] = React.useState(false);
  // Phase 6e.4.2 — the FX-sync plan state and Magic Button mount
  // points moved to ContractListPage. This page no longer holds
  // transient sync state.

  const form = useForm<EngagementCreationFormValues>({
    resolver: zodResolver(engagementCreationSchema),
    defaultValues: {
      clientName: "",
      auditPeriodStart: defaultStart(),
      auditPeriodEnd: defaultEnd(),
      functionalCurrency: "TRY",
      // Phase 6c.2 (Frontend Spec §21.6.2) — no default selection. The
      // form starts with `openingBalanceMode: undefined`; the radio
      // group renders with nothing checked. Zod blocks submit until
      // the auditor picks a mode.
      openingBalanceMode: undefined,
      contractCount: 1,
      openingBalances: undefined,
      closingFxRates: undefined,
    },
    mode: "onBlur",
  });

  const mode = form.watch("openingBalanceMode");

  // MINI_FIX_BP — reactive read of the audit period end so the closing-FX
  // section always fetches TCMB rates for the date currently on screen.
  // `form.getValues` is NOT reactive: editing the DatePicker would otherwise
  // leave a stale ISO date in the prop and TCMB would return rates for the
  // previous value.
  const watchedAuditPeriodEnd = useWatch({
    control: form.control,
    name: "auditPeriodEnd",
  });
  const liveAuditPeriodEndIso =
    watchedAuditPeriodEnd instanceof Date &&
    !Number.isNaN(watchedAuditPeriodEnd.getTime())
      ? isoDate(watchedAuditPeriodEnd)
      : undefined;

  // Phase 6c.2 — slide-down/unmount semantics for the MANUAL inputs.
  // When mode is NOT "MANUAL" the openingBalances are cleared so a
  // later switch back to MANUAL starts with a fresh empty form
  // (audit-grade: no stale values that the auditor might accidentally
  // submit). When mode IS "MANUAL" and no values are present yet,
  // seed the input shape so the controlled inputs render correctly.
  React.useEffect(() => {
    if (mode !== "MANUAL") {
      form.setValue("openingBalances", undefined, { shouldValidate: false });
    } else if (!form.getValues("openingBalances")) {
      form.setValue(
        "openingBalances",
        {
          rouAssetCost: "",
          rouAccumulatedDepreciation: "",
          leaseLiability: "",
        },
        { shouldValidate: false },
      );
    }
  }, [mode, form]);

  // Phase 6a.19a — edit mode bootstrap. Loads the persisted entry on
  // mount and resets the form with its values. A missing entry (manual
  // URL with a bogus id, or one that was already soft-deleted) surfaces
  // a destructive toast and bounces the auditor back to /history rather
  // than leaving them stranded on a blank form.
  React.useEffect(() => {
    if (!isEditMode || !editId) return;
    let cancelled = false;
    void historyStorage
      .getEngagement(editId)
      .then((entry) => {
        if (cancelled) return;
        if (!entry) {
          toast.error(t("engagement.edit.notFound"));
          navigate("/history", { replace: true });
          return;
        }
        setLoadedEntry(entry);
        form.reset({
          clientName: entry.clientName,
          // Phase 6a.23 polish — Finding AG: hydrate via the UTC-safe
          // `isoToDate` helper. `date-fns`'s `parseISO` parses date-only
          // strings in the LOCAL timezone, so on UTC+3 the stored
          // "2025-01-01" would become Date(2024-12-31T21:00:00Z); then
          // `dateToDisplay` (UTC-component read) would render "31.12.2024".
          // `isoToDate` constructs `Date.UTC(...)` directly, keeping the
          // round-trip identity ISO ↔ display. Fallback to defaultStart/
          // End for malformed strings since the RHF schema is `z.date()`.
          auditPeriodStart:
            isoToDate(entry.auditPeriodStart) ?? defaultStart(),
          auditPeriodEnd: isoToDate(entry.auditPeriodEnd) ?? defaultEnd(),
          functionalCurrency:
            entry.functionalCurrency as EngagementCreationFormValues["functionalCurrency"],
          openingBalanceMode: entry.openingBalanceMode,
          contractCount: entry.contractCount,
          openingBalances: entry.openingBalances,
          closingFxRates: entry.closingFxRates,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[engagement edit] load failed", err);
        toast.error(t("engagement.edit.notFound"));
        navigate("/history", { replace: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editId]);

  async function onSubmit(values: EngagementCreationFormValues) {
    setSubmissionError(null);
    try {
      if (isEditMode && loadedEntry) {
        await onSubmitEditMode(values, loadedEntry);
      } else {
        await onSubmitCreateMode(values);
      }
    } catch (err) {
      setSubmissionError(
        err instanceof Error ? err.message : t("error.unknown"),
      );
    }
  }

  async function onSubmitCreateMode(values: EngagementCreationFormValues) {
    const id = `eng-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // Spec §21.7.8 — initial state: N null slots pre-allocated from
    // the auditor's `contractCount`, paired with a matching
    // `contractsDirty` array of `false`s. Append-only is preserved
    // (the "+ Add Contract" button on the list page still works).
    const n = values.contractCount;
    const contracts: Array<null> = Array.from({ length: n }, () => null);
    const contractsDirty: boolean[] = Array.from({ length: n }, () => false);
    // Phase 6c.2 — `openingBalanceMode` is hard-required by Zod via
    // the form-level `superRefine`; the narrowing here is safe at
    // submit time. Persist the openingBalances payload only when the
    // mode is MANUAL.
    const entry: EngagementHistoryEntry = {
      id,
      clientName: values.clientName,
      auditPeriodStart: isoDate(values.auditPeriodStart),
      auditPeriodEnd: isoDate(values.auditPeriodEnd),
      functionalCurrency: values.functionalCurrency,
      openingBalanceMode: values.openingBalanceMode!,
      ...(values.openingBalanceMode === "MANUAL" && values.openingBalances
        ? { openingBalances: values.openingBalances }
        : {}),
      // Phase 6a.23 — `closingFxRates` is collected from the
      // `EngagementClosingFxRatesSection` (derived from FX contracts
      // present in the engagement). On create-mode no contracts
      // exist yet, so this is typically `undefined`; the auditor
      // returns via edit-mode after adding FX contracts.
      ...(values.closingFxRates &&
      Object.keys(values.closingFxRates).length > 0
        ? { closingFxRates: values.closingFxRates }
        : {}),
      contractCount: n,
      contracts,
      contractsDirty,
      status: "draft",
      isStale: false,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await historyStorage.saveEngagement(entry);
    setSubmissionSuccess(true);
    // Brief visual confirmation, then navigate.
    window.setTimeout(() => {
      navigate(`/engagements/${id}/contracts`);
    }, 600);
  }

  async function onSubmitEditMode(
    values: EngagementCreationFormValues,
    existing: EngagementHistoryEntry,
  ) {
    const newStart = isoDate(values.auditPeriodStart);
    const newEnd = isoDate(values.auditPeriodEnd);
    // Phase 6a.19a — currency + opening-balance mode are locked in the
    // UI when `lastResult` exists, so we deliberately ignore any
    // submitted values for those fields and pin them to the persisted
    // values (defensive — covers form-state drift). Without
    // `lastResult` the form values win.
    const calcDone = Boolean(existing.lastResult);
    const nextCurrency = calcDone
      ? existing.functionalCurrency
      : values.functionalCurrency;
    const nextMode = calcDone
      ? existing.openingBalanceMode
      : values.openingBalanceMode!;
    const metadataChanged =
      values.clientName !== existing.clientName ||
      newStart !== existing.auditPeriodStart ||
      newEnd !== existing.auditPeriodEnd ||
      nextCurrency !== existing.functionalCurrency ||
      nextMode !== existing.openingBalanceMode;
    // Phase 6a.19a — stale flag fires only when a calc exists to
    // invalidate. Mirrors ContractListPage edit + 6a.18a transitionToStale.
    const nextIsStale =
      calcDone && metadataChanged ? true : existing.isStale;
    // Phase 6a.23 — keep `closingFxRates` in sync with the form. Drop
    // empty-string entries so a row the auditor cleared doesn't reach
    // the backend as `""` (which the backend regex would reject). If
    // every entry is empty, omit the field entirely.
    const trimmedRates: Record<string, string> = {};
    if (values.closingFxRates) {
      for (const [ccy, rate] of Object.entries(values.closingFxRates)) {
        if (rate && rate.trim().length > 0) trimmedRates[ccy] = rate;
      }
    }
    const closingRatesChanged =
      JSON.stringify(trimmedRates) !==
      JSON.stringify(existing.closingFxRates ?? {});
    const updated: EngagementHistoryEntry = {
      ...existing,
      clientName: values.clientName,
      auditPeriodStart: newStart,
      auditPeriodEnd: newEnd,
      functionalCurrency: nextCurrency,
      openingBalanceMode: nextMode,
      // Preserve openingBalances when the mode stays MANUAL; clear
      // them if the mode flipped away from MANUAL in the form.
      ...(nextMode === "MANUAL" && values.openingBalances
        ? { openingBalances: values.openingBalances }
        : { openingBalances: undefined }),
      closingFxRates:
        Object.keys(trimmedRates).length > 0 ? trimmedRates : undefined,
      // Closing-rate edits also stale a calculated engagement — the
      // backend re-translates the liability using these rates, so a
      // change here invalidates any cached FX-impacted aggregate.
      isStale:
        nextIsStale ||
        (Boolean(existing.lastResult) && closingRatesChanged),
      updatedAt: new Date().toISOString(),
    };
    await historyStorage.saveEngagement(updated);
    toast.success(t("engagement.edit.saveSuccess"));
    navigate("/history");
  }

  // Surface a single warning admonition when the form has any error and
  // at least one field has been touched/blurred.
  const hasErrors = Object.keys(form.formState.errors).length > 0;
  const showValidationWarning = hasErrors && form.formState.isSubmitted;

  // Phase 6a.19a — `lastResult` presence triggers the field-locking
  // mode: currency + opening-balance mode become read-only because
  // changing them after a calculation would silently invalidate every
  // monetary value already persisted. Phase 6a.10 introduces
  // contract-level edits with the matching constraint.
  const lockedByCalculation =
    isEditMode && Boolean(loadedEntry?.lastResult);

  return (
    <PageContainer
      variant="reading"
      className="space-y-6"
      data-testid={isEditMode ? "engagement-edit-page" : "engagement-create-page"}
    >
      <Card data-testid="engagement-creation-card">
        <CardHeader>
          {isEditMode ? (
            <>
              <CardTitle data-testid="engagement-edit-title">
                {t("engagement.edit.title")}
              </CardTitle>
              {loadedEntry && (
                <CardDescription data-testid="engagement-edit-subtitle">
                  {t("engagement.edit.subtitle", {
                    clientName: loadedEntry.clientName,
                  })}
                </CardDescription>
              )}
            </>
          ) : (
            <>
              <CardTitle>{t("engagement.creation.title")}</CardTitle>
              <CardDescription>
                {t("engagement.creation.description")}
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6"
              noValidate
            >
              {/* SPEC-SHARE-B2 — viewer read-only lock (edit mode only). The
                  disabled fieldset is an outer lock layered over the existing
                  `lockedByCalculation` per-field disables. `disabled:opacity-100`
                  keeps the read view legible. */}
              <fieldset
                disabled={viewerReadOnly}
                className="space-y-6 border-0 m-0 p-0 min-w-0 disabled:opacity-100"
                data-testid="engagement-edit-fieldset"
              >
              {viewerReadOnly && (
                <div
                  className="flex items-start gap-2 rounded-md border border-muted bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
                  data-testid="engagement-edit-viewer-note"
                >
                  <Info
                    className="h-4 w-4 mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <p>{t("engagement.edit.viewerNote")}</p>
                </div>
              )}

              {/* Phase 6a.19a — locked-fields advisory, edit mode only,
                  shown when a calculation already exists. Encourages
                  the auditor to duplicate rather than mutate fields
                  that would silently break historical results. */}
              {lockedByCalculation && (
                <div
                  className="flex items-start gap-2 rounded-md border border-muted bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
                  data-testid="engagement-edit-locked-note"
                >
                  <Info
                    className="h-4 w-4 mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <p>{t("engagement.edit.lockedFieldsNote")}</p>
                </div>
              )}

              {/* 1. Client Name */}
              <FormField
                control={form.control}
                name="clientName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("engagement.creation.clientName")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t(
                          "engagement.creation.placeholders.clientName",
                        )}
                        autoComplete="organization"
                        {...field}
                      />
                    </FormControl>
                    <FormMessageT />
                  </FormItem>
                )}
              />

              {/* 2. + 3. Audit Period (start + end) */}
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="auditPeriodStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("engagement.creation.auditPeriodStart")}
                      </FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t(
                            "engagement.creation.placeholders.datePattern",
                          )}
                          aria-invalid={!!form.formState.errors.auditPeriodStart}
                        />
                      </FormControl>
                      <FormMessageT />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="auditPeriodEnd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("engagement.creation.auditPeriodEnd")}
                      </FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t(
                            "engagement.creation.placeholders.datePattern",
                          )}
                          aria-invalid={!!form.formState.errors.auditPeriodEnd}
                        />
                      </FormControl>
                      <FormMessageT />
                    </FormItem>
                  )}
                />
              </div>

              {/* 4. Functional Currency */}
              <FormField
                control={form.control}
                name="functionalCurrency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("engagement.creation.functionalCurrency")}
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={lockedByCalculation}
                    >
                      <FormControl>
                        <SelectTrigger
                          data-testid="engagement-currency-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CURRENCY_CODES.map((code) => (
                          <SelectItem key={code} value={code}>
                            {t(`currency.${code}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessageT />
                  </FormItem>
                )}
              />

              {/* 4c. Phase 6a.23 — engagement-level closing FX rates.
                  Visible currency rows are derived from `loadedEntry.contracts`
                  in edit-mode; in create-mode no contracts exist yet so
                  the section shows the "create-mode" empty admonition. */}
              <EngagementClosingFxRatesSection
                contracts={loadedEntry?.contracts ?? []}
                auditPeriodEnd={
                  liveAuditPeriodEndIso ?? loadedEntry?.auditPeriodEnd
                }
                mode={isEditMode ? "edit" : "create"}
              />

              {/* Phase 6e.4.2 — Magic Button moved to ContractListPage. */}

              {/* 4b. Contract Count (Mini-fix 6a.10/F-1 / Spec §21.7.8) */}
              <FormField
                control={form.control}
                name="contractCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("engagement.creation.contractCount")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={500}
                        step={1}
                        placeholder={t(
                          "engagement.creation.placeholders.contractCount",
                        )}
                        data-testid="contract-count-input"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessageT />
                  </FormItem>
                )}
              />

              {/* 5. Opening Balance Mode — Phase 6c.2 §21.6.2: vertical
                  3-value radio group via the dedicated atom; no default
                  selection. The form-level superRefine blocks submit
                  until the auditor picks one. */}
              <FormField
                control={form.control}
                name="openingBalanceMode"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <OpeningBalanceModeRadio
                        value={field.value}
                        onChange={field.onChange}
                        disabled={lockedByCalculation}
                      />
                    </FormControl>
                    <FormMessageT />
                  </FormItem>
                )}
              />

              {/* 6. Opening Balances (MANUAL only) */}
              {mode === "MANUAL" && (
                <div
                  className="space-y-4 rounded-lg border border-dashed p-4"
                  data-testid="opening-balances-block"
                >
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold">
                      {t("engagement.creation.openingBalancesTitle")}
                    </h3>
                    <Admonition type="info">
                      {t("engagement.creation.openingBalancesInfo")}
                    </Admonition>
                  </div>
                  <FormField
                    control={form.control}
                    name="openingBalances.rouAssetCost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("engagement.creation.rouAssetCost")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            inputMode="decimal"
                            placeholder={t(
                              "engagement.creation.placeholders.openingBalance",
                            )}
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessageT />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="openingBalances.rouAccumulatedDepreciation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("engagement.creation.rouAccumulatedDepreciation")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            inputMode="decimal"
                            placeholder={t(
                              "engagement.creation.placeholders.openingBalance",
                            )}
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessageT />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="openingBalances.leaseLiability"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("engagement.creation.leaseLiability")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            inputMode="decimal"
                            placeholder={t(
                              "engagement.creation.placeholders.openingBalance",
                            )}
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessageT />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {showValidationWarning && (
                <Admonition
                  type="warning"
                  title={t("alerts.validationWarningTitle")}
                >
                  {t("alerts.validationWarningBody")}
                </Admonition>
              )}

              {submissionError && (
                <Admonition type="danger" title={t("alerts.errorTitle")}>
                  {submissionError}
                </Admonition>
              )}

              {submissionSuccess && (
                <Admonition type="success" title={t("alerts.successTitle")}>
                  {t("engagement.creation.successCreated")}
                </Admonition>
              )}

              {/* Phase 6e.4.2 — FxSyncAuditAccordion moved to ContractListPage. */}
              </fieldset>

              {/* Button row lives OUTSIDE the fieldset so a viewer's "back"
                  (cancel) affordance stays clickable. SPEC-SHARE-B2 hides the
                  submit entirely for viewers. */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                {!viewerReadOnly && (
                  <Button
                    type="submit"
                    disabled={form.formState.isSubmitting || submissionSuccess}
                    data-testid="engagement-form-submit"
                  >
                    {isEditMode
                      ? t("engagement.edit.save")
                      : t("engagement.creation.submit")}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(isEditMode ? "/history" : "/")}
                  data-testid="engagement-form-cancel"
                >
                  {isEditMode
                    ? t("engagement.edit.cancel")
                    : t("engagement.creation.cancel")}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
